import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter,
  AlertDialogHeader, AlertDialogOverlay, Button, Flex, IconButton,
  useDisclosure,
} from "@chakra-ui/react";
import { useRef } from "react";
import { useNavigate } from "react-router";
import ButtonWithConfirm from "components/Button/ButtonWithConfirm";
import CaminoTrash from "components/Icon/CaminoTrash";
import { Text14 } from "components/Text";
import { TertiaryButton } from "components/Button";
import { prebuiltTemplatePathByTemplateSummary } from "helpers/url/pages/v2/prebuiltActionPaths";
import { PrebuiltTemplateSummary } from "features/Prebuilt/PrebuiltSharedTemplate/models";
import { useRemoveTemplateFromPath } from "features/Path/hooks";
import { PathId } from "features/Path/models";
import { TemplateSummary } from "../models";
import { useDeleteTemplate } from "../hooks";
import { confirmDeleteTemplateArgs } from "../helpers";

interface TemplateTableButtonsProps {
  templateSummary: TemplateSummary;
}

export function TemplateTableDeleteButton({ templateSummary }: TemplateTableButtonsProps) {
  const { mutate: deleteTemplate } = useDeleteTemplate(templateSummary);
  return (
    <ButtonWithConfirm {...confirmDeleteTemplateArgs(templateSummary)} handleConfirm={() => deleteTemplate()}>
      <IconButton
        aria-label="delete"
        size="xs"
        bgColor="resolveGrey.500"
        _hover={{
          bgColor: "resolveGrey.HOVER",
        }}
        icon={<CaminoTrash />}
      />
    </ButtonWithConfirm>
  );
}

interface TemplateTableButtonsWithRemoveFromPathProps {
  templateSummary: TemplateSummary;
  pathId: PathId;
}

export function TemplateTableButtonsWithRemoveFromPath({ pathId, templateSummary }: TemplateTableButtonsWithRemoveFromPathProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { mutate: removeFromPath } = useRemoveTemplateFromPath({ templatePrefixId: templateSummary?.template?.prefixId, pathId });
  const { mutate: deleteTemplate } = useDeleteTemplate(templateSummary, onClose);
  const templateName = templateSummary?.template?.name || "this template";

  return (
    <>
      <IconButton
        aria-label="delete"
        size="xs"
        bgColor="resolveGrey.500"
        _hover={{ bgColor: "resolveGrey.HOVER" }}
        icon={<CaminoTrash />}
        onClick={onOpen}
      />
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="xl" maxW="sm" py="2">
            <AlertDialogHeader px="6" pt="5" pb="1" fontSize="lg" fontWeight="700" color="content.primary">
              {templateName}
            </AlertDialogHeader>
            <AlertDialogBody px="6" pb="4" pt="0">
              <Text14 color="content.tertiary">
                What would you like to do with this action?
              </Text14>
            </AlertDialogBody>
            <AlertDialogFooter px="6" pb="5" pt="0" flexDirection="column" gap="2">
              <Button
                w="100%"
                h="40px"
                colorScheme="red"
                variant="outline"
                borderColor="red.200"
                fontWeight="medium"
                fontSize="14px"
                onClick={() => deleteTemplate()}
              >
                Delete Permanently
              </Button>
              <Button
                w="100%"
                h="40px"
                colorScheme="brand"
                variant="outline"
                fontWeight="medium"
                fontSize="14px"
                onClick={() => { removeFromPath(); onClose(); }}
              >
                Remove from Path
              </Button>
              <Button
                w="100%"
                h="40px"
                ref={cancelRef}
                variant="ghost"
                color="content.tertiary"
                fontWeight="medium"
                fontSize="14px"
                onClick={onClose}
              >
                Cancel
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

interface PrebuiltTemplateTableButtonsProps {
  templateSummary: PrebuiltTemplateSummary;
}

export function PrebuiltTemplateTableButtons({ templateSummary }: PrebuiltTemplateTableButtonsProps) {
  const navigate = useNavigate();
  return (
    <Flex gap="2">
      <TertiaryButton
        aria-label="edit"
        size="xs"
        onClick={() => navigate(prebuiltTemplatePathByTemplateSummary(templateSummary))}
      >
        View
      </TertiaryButton>
    </Flex>
  );
}
